import React, { useEffect, useState, useCallback } from 'react';
import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import {
  Payment as PaymentIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Cancel as FailedIcon,
  Schedule as PendingIcon,
  Visibility as ViewIcon,
  Close as CloseIcon,
  Receipt as ReceiptIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon
} from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import moment from 'moment-jalaali';
import paymentLogsService from '../services/paymentLogs.service';
import { useAuth } from '../context/AuthContext';
import { ResponsiveTable, CopyableText } from '../components/common';
import type { Column } from '../components/common';
import './PaymentLogsPage.css';

interface PaymentLog {
  _id: string;
  userId?: any;
  orderId?: any;
  method?: string;
  provider?: string;
  action: string;
  status: string;
  amount?: number;
  currency?: string;
  authority?: string;
  refId?: string;
  code?: number;
  message?: string;
  requestBody?: any;
  responseBody?: any;
  callbackQuery?: any;
  ip?: string;
  error?: string;
  meta?: any;
  createdAt: string;
}

const PROVIDER_LABELS: Record<string, string> = {
  zarinpal: 'زرین‌پال',
  payping: 'پی‌پینگ',
  digipay: 'دیجی‌پی',
  snapppay: 'اسنپ‌پی',
  mock: 'تستی',
  withdrawal: 'برداشت',
  referral: 'ریفرال',
};
const INSTRUMENT_LABELS: Record<string, string> = {
  credit: 'خرید اقساطی',
  ipg: 'کارت بانکی',
  wallet: 'کیف پول دیجی‌پی',
  bnpl: 'خرید اقساطی اسنپ‌پی',
};

const PaymentLogsPage: React.FC = () => {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const [filters, setFilters] = useState({
    page: 1,
    limit: 20,
    userId: '',
    orderId: '',
    provider: '',
    action: '',
    status: '',
    authority: '',
    refId: '',
    fromDate: '',
    toDate: '',
    context: ''
  });
  /** فیلترِ اعمال‌شده — fetch فقط با تغییر همین state انجام می‌شود (قبلاً «جستجو» دو درخواست موازی می‌زد و «پاک کردن» refetch نمی‌کرد) */
  const [query, setQuery] = useState(filters);
  const [rows, setRows] = useState<PaymentLog[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(true);
  const [selectedLog, setSelectedLog] = useState<PaymentLog | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [stats, setStats] = useState<any>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page: query.page, limit: query.limit };
      if (query.userId) params.userId = query.userId.trim();
      if (query.orderId) params.orderId = query.orderId.trim();
      if (query.provider) params.provider = query.provider;
      if (query.action) params.action = query.action;
      if (query.status) params.status = query.status;
      if (query.authority) params.authority = query.authority.trim();
      if (query.refId) params.refId = query.refId.trim();
      if (query.context) params.context = query.context;
      const jToISO = (v: string, end = false) => { const m = moment(v, 'jYYYY/jMM/jDD'); return v && m.isValid() ? (end ? m.endOf('day') : m.startOf('day')).toISOString() : undefined; };
      if (query.fromDate) params.fromDate = jToISO(query.fromDate);
      if (query.toDate) params.toDate = jToISO(query.toDate, true);
      // آمار پولی همان بازه (پرداخت/استرداد واقعی به تفکیک درگاه)
      paymentLogsService.getStats({ fromDate: params.fromDate, toDate: params.toDate }).then((s) => setStats(s)).catch(() => setStats(null));

      console.log('Calling paymentLogsService.list with params:', params);
      const res = await paymentLogsService.list(params);
      console.log('Response from paymentLogsService:', res);

      if (res) {
        setRows(res.docs || []);
        setTotal(res.total || 0);
        setTotalPages(res.totalPages || 1);
      } else {
        console.warn('No response from paymentLogsService');
        setRows([]);
        setTotal(0);
        setTotalPages(1);
      }
    } catch (error) {
      console.error('Error fetching payment logs:', error);
      setRows([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, query]);

  const handleSearch = () => {
    const next = { ...filters, page: 1 };
    setFilters(next);
    setQuery(next);
  };

  const EMPTY = { page: 1, limit: 20, userId: '', orderId: '', provider: '', action: '', status: '', authority: '', refId: '', fromDate: '', toDate: '', context: '' };
  const handleReset = () => {
    setFilters(EMPTY);
    setQuery(EMPTY);
  };

  const handleViewDetails = (log: PaymentLog) => {
    setSelectedLog(log);
    setDetailsOpen(true);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('fa-IR');
  };

  const formatAmount = (amount?: number) => {
    if (!amount) return '—';
    return new Intl.NumberFormat('fa-IR').format(amount);
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <Chip size="small" color="success" icon={<SuccessIcon style={{ fontSize: 14 }} />} label="موفق" />;
      case 'pending':
        return <Chip size="small" color="warning" icon={<PendingIcon style={{ fontSize: 14 }} />} label="در انتظار" />;
      case 'failed':
        return <Chip size="small" color="error" icon={<FailedIcon style={{ fontSize: 14 }} />} label="ناموفق" />;
      default:
        return <Chip size="small" label={status} />;
    }
  };

  const getActionBadge = (action: string) => {
    const labels: Record<string, string> = {
      request: 'درخواست',
      callback: 'بازگشت',
      verify: 'تایید',
      inquiry: 'استعلام',
      refund: 'استرداد'
    };
    return <Chip size="small" variant="outlined" color="info" label={labels[action] || action} />;
  };

  const getUserDisplay = (user: any) => {
    if (!user) return '—';
    if (typeof user === 'string') return user;
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{user.name || 'کاربر'}</Typography>
        {user.email && (
          <Typography variant="caption" color="text.secondary">{user.email}</Typography>
        )}
      </Box>
    );
  };

  const getOrderDisplay = (order: any) => {
    if (!order) return '—';
    if (typeof order === 'string') return <CopyableText value={order.slice(-8)} mono />;
    return <CopyableText value={order.orderNumber || order._id?.slice(-8) || '—'} mono />;
  };

  // Calculate stats from rows (simple version)
  const successCount = rows.filter(r => r.status === 'success').length;
  const pendingCount = rows.filter(r => r.status === 'pending').length;
  const failedCount = rows.filter(r => r.status === 'failed').length;

  const columns: Column<PaymentLog>[] = [
    {
      id: 'createdAt',
      label: 'تاریخ',
      priority: 2,
      format: (v) => formatDate(v as string),
    },
    {
      id: 'userId',
      label: 'کاربر',
      priority: 1,
      format: (v) => getUserDisplay(v),
    },
    {
      id: 'orderId',
      label: 'سفارش',
      priority: 5,
      format: (v) => getOrderDisplay(v),
    },
    {
      id: 'action',
      label: 'عملیات',
      priority: 4,
      format: (v) => getActionBadge(v as string),
    },
    {
      id: 'status',
      label: 'وضعیت',
      priority: 1,
      format: (v) => getStatusBadge(v as string),
    },
    {
      id: 'amount',
      label: 'مبلغ (تومان)',
      priority: 3,
      align: 'right',
      format: (v) => formatAmount(v as number | undefined),
    },
    {
      id: 'provider',
      label: 'درگاه',
      priority: 6,
      detail: true,
      format: (v) => (v ? <Chip size="small" variant="outlined" label={PROVIDER_LABELS[v as string] || (v as string)} /> : '—'),
    },
    {
      id: 'refId',
      label: 'Ref ID',
      detail: true,
      format: (v) => (v ? <CopyableText value={v as string} mono truncate maxWidth={160} /> : '—'),
    },
    {
      id: 'message',
      label: 'پیام',
      detail: true,
      format: (_v, row) => (
        <Typography
          variant="body2"
          title={row.message || row.error}
          sx={{
            maxWidth: 320,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {row.message || row.error || '—'}
        </Typography>
      ),
    },
  ];

  const renderActions = (row: PaymentLog) => (
    <Tooltip title="مشاهده جزئیات">
      <IconButton size="small" color="primary" onClick={() => handleViewDetails(row)}>
        <ViewIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  return (
    <>
      <Helmet>
        <title>لاگ‌های پرداخت</title>
      </Helmet>

      <div className="payment-logs-page">
        {/* Header */}
        <div className="payment-logs-header">
          <div className="payment-logs-title">
            <h1>
              <PaymentIcon />
              لاگ‌های پرداخت
            </h1>
            <span className="payment-logs-subtitle">مشاهده و بررسی تراکنش‌های پرداخت</span>
          </div>

          <div className="header-actions">
            <button className="header-btn" onClick={fetchData} disabled={loading}>
              <RefreshIcon style={{ fontSize: 18 }} />
              بروزرسانی
            </button>
          </div>
        </div>

        {/* Stats Row */}
        <div className="stats-row">
          <div className="stat-card">
            <div className="stat-icon info">
              <ReceiptIcon />
            </div>
            <div className="stat-info">
              <span className="stat-label">کل تراکنش‌ها</span>
              <span className="stat-value">{new Intl.NumberFormat('fa-IR').format(total)}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon success">
              <SuccessIcon />
            </div>
            <div className="stat-info">
              <span className="stat-label">پرداخت‌های موفق درگاه{query.fromDate || query.toDate ? ' (بازه)' : ' (کل)'}</span>
              <span className="stat-value">{stats?.money ? `${new Intl.NumberFormat('fa-IR').format(stats.money.totals.paymentsAmount)} تومان` : '—'}</span>
              <span style={{ fontSize: 10, color: 'var(--color-text-secondary)' }}>{stats?.money ? `${new Intl.NumberFormat('fa-IR').format(stats.money.totals.paymentsCount)} پرداخت · این صفحه: ${new Intl.NumberFormat('fa-IR').format(successCount)} موفق` : ''}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon warning">
              <PendingIcon />
            </div>
            <div className="stat-info">
              <span className="stat-label">استرداد واقعی از درگاه</span>
              <span className="stat-value">{stats?.money ? `${new Intl.NumberFormat('fa-IR').format(stats.money.totals.refundsAmount)} تومان` : '—'}</span>
              <span style={{ fontSize: 10, color: 'var(--color-text-secondary)' }}>{stats?.money ? `${new Intl.NumberFormat('fa-IR').format(stats.money.totals.refundsCount)} استرداد · برگشت به کیف پول ${new Intl.NumberFormat('fa-IR').format(stats.money.totals.walletRefundsAmount)} تومان` : `این صفحه: ${new Intl.NumberFormat('fa-IR').format(pendingCount)} در انتظار`}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon danger">
              <FailedIcon />
            </div>
            <div className="stat-info">
              <span className="stat-label">خالص درگاه‌ها</span>
              <span className="stat-value">{stats?.money ? `${new Intl.NumberFormat('fa-IR').format(stats.money.totals.paymentsAmount - stats.money.totals.refundsAmount)} تومان` : '—'}</span>
              <span style={{ fontSize: 10, color: 'var(--color-text-secondary)' }}>{stats?.money ? (stats.money.rows || []).slice(0, 4).map((r: any) => `${PROVIDER_LABELS[r.provider] || r.provider}: ${new Intl.NumberFormat('fa-IR').format(r.paymentsAmount)}`).join(' · ') : `این صفحه: ${new Intl.NumberFormat('fa-IR').format(failedCount)} ناموفق`}</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-section">
          <div className="filters-header">
            <div className="filters-title">
              <FilterIcon />
              فیلترها
            </div>
            <button className="filters-toggle" onClick={() => setShowFilters(!showFilters)}>
              {showFilters ? <><CollapseIcon /> بستن</> : <><ExpandIcon /> باز کردن</>}
            </button>
          </div>

          {showFilters && (
            <>
              <div className="filters-grid">
                <div className="filter-group">
                  <label className="filter-label">شناسه کاربر</label>
                  <input
                    className="filter-input"
                    placeholder="شناسه کاربر..."
                    value={filters.userId}
                    onChange={(e) => setFilters(f => ({ ...f, userId: e.target.value }))}
                  />
                </div>
                <div className="filter-group">
                  <label className="filter-label">شناسه سفارش</label>
                  <input
                    className="filter-input"
                    placeholder="شناسه سفارش..."
                    value={filters.orderId}
                    onChange={(e) => setFilters(f => ({ ...f, orderId: e.target.value }))}
                  />
                </div>
                <div className="filter-group">
                  <label className="filter-label">درگاه</label>
                  <select
                    className="filter-select"
                    value={filters.provider}
                    onChange={(e) => setFilters(f => ({ ...f, provider: e.target.value }))}
                  >
                    <option value="">همه</option>
                    <option value="zarinpal">زرین‌پال</option>
                    <option value="snapppay">اسنپ‌پی</option>
                    <option value="digipay">دیجی‌پی</option>
                    <option value="payping">پی‌پینگ</option>
                    <option value="withdrawal">کارمزد برداشت (درآمد)</option>
                    <option value="referral">پاداش ریفرال</option>
                    <option value="mock">تست</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label className="filter-label">نوع عملیات</label>
                  <select
                    className="filter-select"
                    value={filters.action}
                    onChange={(e) => setFilters(f => ({ ...f, action: e.target.value }))}
                  >
                    <option value="">همه</option>
                    <option value="request">درخواست</option>
                    <option value="callback">بازگشت</option>
                    <option value="verify">تایید</option>
                    <option value="inquiry">استعلام</option>
                    <option value="refund">استرداد</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label className="filter-label">وضعیت</label>
                  <select
                    className="filter-select"
                    value={filters.status}
                    onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
                  >
                    <option value="">همه</option>
                    <option value="success">موفق</option>
                    <option value="pending">در انتظار</option>
                    <option value="failed">ناموفق</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label className="filter-label">از تاریخ (شمسی)</label>
                  <input className="filter-input" placeholder="۱۴۰۴/۰۱/۰۱" value={filters.fromDate} onChange={(e) => setFilters(f => ({ ...f, fromDate: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} dir="ltr" />
                </div>
                <div className="filter-group">
                  <label className="filter-label">تا تاریخ (شمسی)</label>
                  <input className="filter-input" placeholder="۱۴۰۴/۱۲/۲۹" value={filters.toDate} onChange={(e) => setFilters(f => ({ ...f, toDate: e.target.value }))} onKeyDown={(e) => e.key === 'Enter' && handleSearch()} dir="ltr" />
                </div>
                <div className="filter-group">
                  <label className="filter-label">زمینه</label>
                  <select className="filter-select" value={filters.context} onChange={(e) => setFilters(f => ({ ...f, context: e.target.value }))}>
                    <option value="">همه</option>
                    <option value="order">سفارش</option>
                    <option value="wallet_charge">شارژ کیف پول</option>
                    <option value="banner_reservation">رزرو بنر</option>
                    <option value="seller_plan">پلن فروشنده</option>
                    <option value="seller_withdrawal_payout">واریز به فروشنده</option>
                    <option value="seller_withdrawal_fee">کارمزد برداشت</option>
                    <option value="withdrawal_fee_share">سهم ریفرال</option>
                  </select>
                </div>
                <div className="filter-group">
                  <label className="filter-label">Authority</label>
                  <input
                    className="filter-input"
                    placeholder="کد authority..."
                    value={filters.authority}
                    onChange={(e) => setFilters(f => ({ ...f, authority: e.target.value }))}
                  />
                </div>
                <div className="filter-group">
                  <label className="filter-label">Ref ID</label>
                  <input
                    className="filter-input"
                    placeholder="شماره مرجع..."
                    value={filters.refId}
                    onChange={(e) => setFilters(f => ({ ...f, refId: e.target.value }))}
                  />
                </div>
              </div>

              <div className="filters-actions">
                <button className="filter-btn primary" onClick={handleSearch}>
                  <SearchIcon style={{ fontSize: 18 }} />
                  جستجو
                </button>
                <button className="filter-btn secondary" onClick={handleReset}>
                  پاک کردن فیلترها
                </button>
              </div>
            </>
          )}
        </div>

        {/* Table */}
        <div className="table-section">
          <div className="table-header">
            <span className="table-title">لیست تراکنش‌ها</span>
            <span className="table-count">{new Intl.NumberFormat('fa-IR').format(total)} تراکنش</span>
          </div>

          <ResponsiveTable<PaymentLog>
            columns={columns}
            data={rows}
            keyField="_id"
            loading={(authLoading || loading) && !rows.length}
            emptyMessage="تراکنشی یافت نشد"
            renderActions={renderActions}
            pagination
            page={query.page - 1}
            rowsPerPage={query.limit}
            totalCount={total}
            rowsPerPageOptions={[10, 20, 50, 100]}
            onPageChange={(p) => { const next = { ...query, page: p + 1 }; setFilters(next); setQuery(next); }}
            onRowsPerPageChange={(n) => { const next = { ...query, limit: n, page: 1 }; setFilters(next); setQuery(next); }}
          />
        </div>
      </div>

      {/* Details Dialog */}
      {detailsOpen && selectedLog && (
        <div className="dialog-overlay" onClick={() => setDetailsOpen(false)}>
          <div className="dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2><ReceiptIcon /> جزئیات تراکنش</h2>
              <button className="dialog-close" onClick={() => setDetailsOpen(false)}>
                <CloseIcon />
              </button>
            </div>
            <div className="dialog-content">
              <div className="detail-grid">
                <div className="detail-item">
                  <span className="detail-label">تاریخ</span>
                  <span className="detail-value">{formatDate(selectedLog.createdAt)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">وضعیت</span>
                  <span className="detail-value">{getStatusBadge(selectedLog.status)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">عملیات</span>
                  <span className="detail-value">{getActionBadge(selectedLog.action)}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">مبلغ</span>
                  <span className="detail-value">{formatAmount(selectedLog.amount)} تومان</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">درگاه</span>
                  <span className="detail-value">{selectedLog.provider || '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">روش پرداخت</span>
                  <span className="detail-value">{selectedLog.method || '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Authority</span>
                  <span className="detail-value code">{selectedLog.authority || '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Ref ID</span>
                  <span className="detail-value code">{selectedLog.refId || '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">کد پاسخ</span>
                  <span className="detail-value">{selectedLog.code ?? '—'}</span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">IP</span>
                  <span className="detail-value code">{selectedLog.ip || '—'}</span>
                </div>
                {/* دیجی‌پی: تفکیک مبلغ پایه + کارمزد اقساطی + ابزار پرداخت */}
                {selectedLog.meta?.surchargeAmount !== undefined && (
                  <>
                    <div className="detail-item">
                      <span className="detail-label">مبلغ سفارش (پایه)</span>
                      <span className="detail-value">{formatAmount(selectedLog.meta.baseAmount)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">کارمزد {INSTRUMENT_LABELS[selectedLog.meta.instrument] || ''} ({selectedLog.meta.surchargePercent ?? 0}٪)</span>
                      <span className="detail-value">{formatAmount(selectedLog.meta.surchargeAmount)}</span>
                    </div>
                    {selectedLog.meta.surchargeAdjustedAmount > 0 && (
                      <div className="detail-item">
                        <span className="detail-label">کارمزد برگشتی به کیف پول</span>
                        <span className="detail-value" style={{ color: 'var(--color-warning-dark)' }}>{formatAmount(selectedLog.meta.surchargeAdjustedAmount)}</span>
                      </div>
                    )}
                    <div className="detail-item">
                      <span className="detail-label">ابزار پرداخت</span>
                      <span className="detail-value">
                        {INSTRUMENT_LABELS[selectedLog.meta.actualInstrument || selectedLog.meta.instrument] || selectedLog.meta.instrument || '—'}
                        {selectedLog.meta.actualInstrument && selectedLog.meta.actualInstrument !== selectedLog.meta.instrument ? ` (انتخاب خریدار: ${INSTRUMENT_LABELS[selectedLog.meta.instrument] || selectedLog.meta.instrument})` : ''}
                        {selectedLog.meta.pspName ? ` — ${selectedLog.meta.pspName}` : ''}
                      </span>
                    </div>
                  </>
                )}
                <div className="detail-item full-width">
                  <span className="detail-label">پیام</span>
                  <span className="detail-value">{selectedLog.message || '—'}</span>
                </div>
                {selectedLog.error && (
                  <div className="detail-item full-width">
                    <span className="detail-label">خطا</span>
                    <span className="detail-value" style={{ color: 'var(--color-error-dark)' }}>{selectedLog.error}</span>
                  </div>
                )}
                {selectedLog.requestBody && (
                  <div className="detail-item full-width">
                    <span className="detail-label">Request Body</span>
                    <span className="detail-value code">
                      <pre>{JSON.stringify(selectedLog.requestBody, null, 2)}</pre>
                    </span>
                  </div>
                )}
                {selectedLog.responseBody && (
                  <div className="detail-item full-width">
                    <span className="detail-label">Response Body</span>
                    <span className="detail-value code">
                      <pre>{JSON.stringify(selectedLog.responseBody, null, 2)}</pre>
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PaymentLogsPage;
